import axios from 'axios';
import config from '../config';

import { store } from '../store';
import { showError } from '../utils';
import { mintAuthOtt } from './auth';

// Generic network call function to handle common API request patterns
function networkCall({ method, url, headers, params, data, ...extraOptions }) {
  return axios({
    method,
    url,
    headers,
    params,
    data,
    ...extraOptions,
  })
    .then((resp) => {
      const responseBody = resp.data;
      if (
        responseBody.message === 'success' ||
        responseBody.status === 'success'
      ) {
        return responseBody;
      } else {
        const error = new Error('Invalid response');
        error.data = responseBody;
        throw error;
      }
    })
    .catch((err) => {
      showError(err);
      throw err;
    });
}

export function fetchPreviousDentalAppointments(authToken) {
  return networkCall({
    method: 'get',
    url: `${config.dentalBaseServer}/care/cashless-dental`,
    headers: {
      authorization: authToken,
    },
  });
}

export function fetchUserDentalAppointments(authToken) {
  return networkCall({
    method: 'get',
    url: `${config.dentalBaseServer}/cashless-dental/mdp/user-past-appointments`,
    headers: {
      authorization: authToken,
    },
  });
}

export function getDentalRequestStatus(appointmentId, authToken) {
  return networkCall({
    method: 'get',
    url: `${config.dentalServer}/cashless-dental/get-appointment-status`,
    headers: {
      authorization: authToken,
    },
    params: { requestId: appointmentId },
  });
}

export function searchDentists(latitude, longitude, authToken) {
  return networkCall({
    method: 'get',
    url: `${config.dentalServer}/cashless-dental/get-clinics`,
    headers: {
      authorization: authToken,
    },
    params: {
      lat: latitude === -1 ? undefined : latitude,
      long: longitude === -1 ? undefined : longitude,
    },
  });
}

export function getDentalRelatives() {
  return networkCall({
    method: 'get',
    url: `${config.dentalBaseServer}/care/relatives?t=dental`,
    headers: {
      authorization: store.getState().user.authToken,
    },
  });
}

export function fetchDentalSlots(authToken, doctorId, clinicId, partnerId) {
  return networkCall({
    method: 'get',
    url: `${config.dentalServer}/cashless-dental/get-slots`,
    headers: {
      authorization: authToken,
    },
    params: { doctorId, clinicId, partnerId },
  });
}

export function getDentalInvoice(
  clinicId,
  bookingType = 'offline',
  price,
  relativeId,
  authToken
) {
  return networkCall({
    method: 'get',
    url: `${config.dentalServer}/cashless-dental/checkout`,
    headers: {
      authorization: authToken,
    },
    params: {
      clinicId,
      bookingType,
      // price,
      relativeId: relativeId === 'self' ? undefined : relativeId,
    },
  });
}

export function cancelDentalSlotRequest(
  appointmentId,
  providerId,
  reason,
  authToken
) {
  return networkCall({
    method: 'post',
    url: `${config.dentalServer}/cashless-dental/cancel-appointment`,
    headers: {
      authorization: authToken,
    },
    data: {
      appointmentId,
      providerId,
      reason,
    },
  });
}

export function uploadDentalPrescription(file, authToken) {
  return networkCall({
    method: 'post',
    url: `${config.dentalServer}/cashless-dental/user/upload-prescription`,
    headers: {
      authorization: authToken,
    },
    data: file,
  });
}

export function bookDentalAppointment(
  relativeId,
  providerId,
  clinicId,
  latitude,
  longitude,
  clinicType,
  datekey,
  slotId,
  finalFee,
  history,
  authToken
) {
  const headers = {
    authorization: authToken,
  };
  const redirectToPaymentGateway = async () => {
    const ott = await mintAuthOtt(authToken, 'payment');
    const authParam = ott ? `ott=${ott}` : `auth=${authToken.split(' ')[1]}`;
    let url =
      relativeId === 'self'
        ? `${config.dentalServer}/cashless-dental/new-appointment?providerId=${providerId}&clinicId=${clinicId}&bookingType=${clinicType}&dateKey=${datekey}&slotId=${slotId}&latitude=${latitude}&longitude=${longitude}&${authParam}`
        : `${config.dentalServer}/cashless-dental/new-appointment?providerId=${providerId}&clinicId=${clinicId}&bookingType=${clinicType}&dateKey=${datekey}&slotId=${slotId}&latitude=${latitude}&longitude=${longitude}&relativeId=${relativeId}&${authParam}`;

    return window.location.assign(url);
  };

  if (finalFee === true || finalFee.toString() === '0') {
    return redirectToPaymentGateway();
  }

  const url =
    relativeId === 'self'
      ? `${config.dentalServer}/cashless-dental/new-appointment?providerId=${providerId}&clinicId=${clinicId}&bookingType=${clinicType}&dateKey=${datekey}&slotId=${slotId}&latitude=${latitude}&longitude=${longitude}`
      : `${config.dentalServer}/cashless-dental/new-appointment?providerId=${providerId}&clinicId=${clinicId}&bookingType=${clinicType}&dateKey=${datekey}&slotId=${slotId}&latitude=${latitude}&longitude=${longitude}&relativeId=${relativeId}`;
  return window.PhonePe.PhonePe.build(window.PhonePe.Constants.Species.web)
    .then((sdk) => {
      // Call the API to fetch the data context
      return axios
        .get(url, {
          headers,
        })
        .then((res) => {
          const responseBody = res.data;
          if (responseBody.message === 'success') {
            const context = responseBody.data;
            if (sdk.isMethodSupported('openPaymentsPage')) {
              history.replace('/processing-payment', {
                orderType: 'dental',
                transactionId: responseBody.transactionId,
                successPage: `dental/preview`,
                failurePage: `dental/preview`,
                failureStateParameters: {
                  paymentFailed: true,
                },
              });
              return sdk.openPaymentsPage(
                'Visit',
                context,
                `${config.rootUrl}dental/preview`,
                'https://getvisitapp.com/img/png/Visit_logo.png',
                [
                  {
                    'Order For': 'Doctor Consultation',
                  },
                ]
              );
            }
            throw new Error('Phonepe method not supported');
          } else if (responseBody.message === 'failure') {
            history.push('/home', {
              bookingFailed: true,
              errorMessage: responseBody.errorMessage,
            });
            return;
          } else {
            throw new Error('Failed to get payment context');
          }
        });
    })
    .catch((err) => {
      try {
        const parsedError = JSON.parse(err);
        if (
          parsedError &&
          (parsedError.error_code === 'PAYMENT_DISMISS' ||
            parsedError.error_code === 'PAYMENT_TIMEOUT')
        ) {
          store.dispatch({
            type: 'SHOW_ERROR',
            payload: {
              errorTitle: 'Something went wrong',
              errorDesc: 'Your payment failed. Please retry your payment.',
            },
          });
          history.push('/home', {
            paymentFailed: true,
          });
          return;
        }
        // in case of android, it could still be a null error.
        if (parsedError === 'null' || parsedError === null) {
          store.dispatch({
            type: 'SHOW_ERROR',
            payload: {
              errorTitle: 'Something went wrong',
              errorDesc: 'Your payment failed. Please retry your payment.',
            },
          });
          history.push('/home', {
            paymentFailed: true,
          });
          return;
        }
      } catch (e) {
        //  Since we couldn't parse the error, it means it's not a string and hence not a phonepe dismiss/timeout error.
        // Not on phonepe in-app or some other error.
        // send to payment
        return redirectToPaymentGateway();
      }
    });
}

export function procedureRequest(authToken, requestId, ids) {
  const body = {
    requestId: requestId,
  };

  // Add procedureIds only if id is not null
  if (ids.length) {
    body.procedureIds = String(ids); // Ensures the id is in string format
  }

  return networkCall({
    method: 'post',
    url: `${config.dentalServer}/cashless-dental/procedure-requested`,
    headers: {
      Authorization: authToken,
    },
    data: body,
  });
}

export function procedureWalletDeduction(authToken, requestId, ids, history) {
  const procedureIds = ids.length ? String(ids) : '';
  const queryParams = procedureIds
    ? `requestId=${requestId}&procedureIds=${procedureIds}`
    : `requestId=${requestId}`;

  const headers = {
    authorization: authToken,
  };

  const redirectToPaymentGateway = async () => {
    const ott = await mintAuthOtt(authToken, 'payment');
    const authParam = ott ? `ott=${ott}` : `auth=${authToken.split(' ')[1]}`;
    const url = `${config.dentalServer}/cashless-dental/procedures-wallet-deduction?${queryParams}&${authParam}`;
    window.location.assign(url);
  };

  return window.PhonePe.PhonePe.build(window.PhonePe.Constants.Species.web)
    .then((sdk) => {
      return axios
        .get(
          `${config.dentalServer}/cashless-dental/procedures-wallet-deduction?${queryParams}`,
          { headers }
        )
        .then((res) => {
          const responseBody = res.data;
          if (responseBody.message === 'success') {
            const context = responseBody.data;
            if (sdk.isMethodSupported('openPaymentsPage')) {
              history.replace('/processing-payment', {
                orderType: 'dental',
                transactionId: responseBody.transactionId,
                successPage: 'dental/preview',
                failurePage: 'dental/preview',
                failureStateParameters: {
                  paymentFailed: true,
                },
              });
              return sdk.openPaymentsPage(
                'Visit',
                context,
                `${config.rootUrl}dental/preview`,
                'https://getvisitapp.com/img/png/Visit_logo.png',
                [{ 'Order For': 'Procedure Payment' }]
              );
            }
            throw new Error('Phonepe method not supported');
          } else if (responseBody.message === 'failure') {
            history.push('/home', {
              bookingFailed: true,
              errorMessage: responseBody.errorMessage,
            });
            return;
          } else {
            throw new Error('Failed to get payment context');
          }
        });
    })
    .catch((err) => {
      try {
        const parsedError = JSON.parse(err);
        if (
          parsedError &&
          (parsedError.error_code === 'PAYMENT_DISMISS' ||
            parsedError.error_code === 'PAYMENT_TIMEOUT')
        ) {
          store.dispatch({
            type: 'SHOW_ERROR',
            payload: {
              errorTitle: 'Something went wrong',
              errorDesc: 'Your payment failed. Please retry your payment.',
            },
          });
          history.push('/home', {
            paymentFailed: true,
          });
          return;
        }
        if (parsedError === 'null' || parsedError === null) {
          store.dispatch({
            type: 'SHOW_ERROR',
            payload: {
              errorTitle: 'Something went wrong',
              errorDesc: 'Your payment failed. Please retry your payment.',
            },
          });
          history.push('/home', {
            paymentFailed: true,
          });
          return;
        }
      } catch (e) {
        return redirectToPaymentGateway();
      }
    });
}

export function initiateConsultationPayment(requestId, authToken, history) {
  const headers = {
    authorization: authToken,
  };

  const redirectToPaymentGateway = async () => {
    const ott = await mintAuthOtt(authToken, 'payment');
    const authParam = ott ? `ott=${ott}` : `auth=${authToken.split(' ')[1]}`;
    const url = `${config.dentalServer}/cashless-dental/consultation-payment?requestId=${requestId}&${authParam}`;
    window.location.assign(url);
  };

  return window.PhonePe.PhonePe.build(window.PhonePe.Constants.Species.web)
    .then((sdk) => {
      return axios
        .get(
          `${config.dentalServer}/cashless-dental/consultation-payment?requestId=${requestId}`,
          { headers }
        )
        .then((res) => {
          const responseBody = res.data;
          if (responseBody.message === 'success') {
            const context = responseBody.data;
            if (sdk.isMethodSupported('openPaymentsPage')) {
              history.replace('/processing-payment', {
                orderType: 'dental',
                transactionId: responseBody.transactionId,
                successPage: 'dental/preview',
                failurePage: 'dental/preview',
                failureStateParameters: {
                  paymentFailed: true,
                },
              });
              return sdk.openPaymentsPage(
                'Visit',
                context,
                `${config.rootUrl}dental/preview`,
                'https://getvisitapp.com/img/png/Visit_logo.png',
                [{ 'Order For': 'Consultation Fee' }]
              );
            }
            throw new Error('Phonepe method not supported');
          } else if (responseBody.message === 'failure') {
            history.push('/home', {
              bookingFailed: true,
              errorMessage: responseBody.errorMessage,
            });
            return;
          } else {
            throw new Error('Failed to get payment context');
          }
        });
    })
    .catch((err) => {
      try {
        const parsedError = JSON.parse(err);
        if (
          parsedError &&
          (parsedError.error_code === 'PAYMENT_DISMISS' ||
            parsedError.error_code === 'PAYMENT_TIMEOUT')
        ) {
          store.dispatch({
            type: 'SHOW_ERROR',
            payload: {
              errorTitle: 'Something went wrong',
              errorDesc: 'Your payment failed. Please retry your payment.',
            },
          });
          history.push('/home', {
            paymentFailed: true,
          });
          return;
        }
        if (parsedError === 'null' || parsedError === null) {
          store.dispatch({
            type: 'SHOW_ERROR',
            payload: {
              errorTitle: 'Something went wrong',
              errorDesc: 'Your payment failed. Please retry your payment.',
            },
          });
          history.push('/home', {
            paymentFailed: true,
          });
          return;
        }
      } catch (e) {
        return redirectToPaymentGateway();
      }
    });
}
